import { useState, useMemo } from 'react'
import {
  Table,
  Text,
  Badge,
  Progress,
  Group,
  Skeleton,
  SegmentedControl,
  Collapse,
  Stack,
  UnstyledButton,
  Box,
} from '@mantine/core'
import {
  IconArrowUp,
  IconArrowDown,
  IconSelector,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ZabbixHost {
  id: string
  name: string
  status: 'up' | 'down' | 'problem' | 'disabled'
  cpu_percent: number
  memory_percent: number
  disk_percent: number
  network_in_bps: number
  network_out_bps: number
}

interface ZabbixHostsData {
  hosts: ZabbixHost[]
  collected_at: string
}

type SortCol = keyof ZabbixHost
type SortDir = 'asc' | 'desc'
type StatusFilter = 'all' | 'up' | 'down' | 'problem' | 'disabled'

interface ZabbixHostsTableProps {
  tenantId: string
}

// ── Utilities ──────────────────────────────────────────────────────────────────

export function formatBps(bps: number | undefined | null): string {
  const v = bps ?? 0
  if (v < 1000) return `${v.toFixed(1)} bps`
  if (v < 1_000_000) return `${(v / 1000).toFixed(1)} Kbps`
  return `${(v / 1_000_000).toFixed(1)} Mbps`
}

// ── MetricBar ──────────────────────────────────────────────────────────────────

function MetricBar({ value }: { value: number | undefined | null }) {
  const v = value ?? 0
  const color = v < 70 ? 'green' : v < 90 ? 'yellow' : 'red'
  return (
    <Group gap="xs" wrap="nowrap">
      <Progress value={v} color={color} size="sm" style={{ flex: 1, minWidth: 60 }} />
      <Text size="xs" fw={500} style={{ whiteSpace: 'nowrap' }}>
        {v.toFixed(1)}%
      </Text>
    </Group>
  )
}

// ── Hook (hosts endpoint) ──────────────────────────────────────────────────────

function useZabbixHosts(tenantId: string) {
  return useQuery<ZabbixHostsData>({
    queryKey: ['zabbix-hosts', tenantId],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/zabbix/hosts`)
      return res.data
    },
    enabled: !!tenantId,
    staleTime: 300_000,
  })
}

// ── Sort header ────────────────────────────────────────────────────────────────

function SortHeader({
  label,
  col,
  activeCol,
  dir,
  onSort,
}: {
  label: string
  col: SortCol
  activeCol: SortCol
  dir: SortDir
  onSort: (col: SortCol) => void
}) {
  const Icon = col === activeCol
    ? dir === 'asc' ? IconArrowUp : IconArrowDown
    : IconSelector

  return (
    <UnstyledButton onClick={() => onSort(col)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <Text size="sm" fw={600}>{label}</Text>
      <Icon size={14} />
    </UnstyledButton>
  )
}

// ── Status badge ───────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<ZabbixHost['status'], string> = {
  up: 'green',
  down: 'red',
  problem: 'orange',
  disabled: 'gray',
}

const STATUS_LABEL: Record<ZabbixHost['status'], string> = {
  up: 'UP',
  down: 'DOWN',
  problem: 'PROBLEM',
  disabled: 'DESABILITADO',
}

function StatusBadge({ status }: { status: ZabbixHost['status'] }) {
  return (
    <Badge size="sm" color={STATUS_COLOR[status]} variant={status === 'disabled' ? 'light' : 'filled'}>
      {STATUS_LABEL[status]}
    </Badge>
  )
}

// ── Compare helper ─────────────────────────────────────────────────────────────

function compare(a: ZabbixHost, b: ZabbixHost, col: SortCol, dir: SortDir): number {
  const av = a[col]
  const bv = b[col]
  const cmp = typeof av === 'string' ? av.localeCompare(bv as string) : (av as number) - (bv as number)
  return dir === 'asc' ? cmp : -cmp
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ZabbixHostsTable({ tenantId }: ZabbixHostsTableProps) {
  const { data, isLoading } = useZabbixHosts(tenantId)
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>({ col: 'name', dir: 'asc' })
  const [filter, setFilter] = useState<StatusFilter>('all')
  const [expandedHostId, setExpandedHostId] = useState<string | null>(null)

  const handleSort = (col: SortCol) => {
    setSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' },
    )
  }

  const hosts = useMemo(() => {
    if (!data?.hosts) return []
    const filtered = filter === 'all' ? data.hosts : data.hosts.filter(h => h.status === filter)
    return [...filtered].sort((a, b) => compare(a, b, sort.col, sort.dir))
  }, [data?.hosts, filter, sort])

  const COLUMNS = 7

  if (isLoading) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height={36} />
        ))}
      </Stack>
    )
  }

  const headerProps = { activeCol: sort.col, dir: sort.dir, onSort: handleSort }

  return (
    <Stack gap="md">
      <SegmentedControl
        value={filter}
        onChange={v => setFilter(v as StatusFilter)}
        data={[
          { label: 'Todos', value: 'all' },
          { label: 'Up', value: 'up' },
          { label: 'Down', value: 'down' },
          { label: 'Problem', value: 'problem' },
          { label: 'Desabilitado', value: 'disabled' },
        ]}
        size="sm"
      />

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th><SortHeader label="Nome" col="name" {...headerProps} /></Table.Th>
            <Table.Th><SortHeader label="Status" col="status" {...headerProps} /></Table.Th>
            <Table.Th><SortHeader label="CPU%" col="cpu_percent" {...headerProps} /></Table.Th>
            <Table.Th><SortHeader label="Memória%" col="memory_percent" {...headerProps} /></Table.Th>
            <Table.Th><SortHeader label="Disco%" col="disk_percent" {...headerProps} /></Table.Th>
            <Table.Th><SortHeader label="Rede In" col="network_in_bps" {...headerProps} /></Table.Th>
            <Table.Th><SortHeader label="Rede Out" col="network_out_bps" {...headerProps} /></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {hosts.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={COLUMNS}>
                <Text ta="center" c="dimmed" py="xl">
                  Nenhum host encontrado para o filtro selecionado.
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            hosts.map(host => (
              <HostRow
                key={host.id}
                host={host}
                expanded={expandedHostId === host.id}
                onToggle={() => setExpandedHostId(prev => (prev === host.id ? null : host.id))}
                columns={COLUMNS}
              />
            ))
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  )
}

// ── Host row ───────────────────────────────────────────────────────────────────

function HostRow({
  host,
  expanded,
  onToggle,
  columns,
}: {
  host: ZabbixHost
  expanded: boolean
  onToggle: () => void
  columns: number
}) {
  return (
    <>
      <Table.Tr onClick={onToggle} style={{ cursor: 'pointer' }}>
        <Table.Td><Text size="sm" fw={500}>{host.name}</Text></Table.Td>
        <Table.Td><StatusBadge status={host.status} /></Table.Td>
        <Table.Td><MetricBar value={host.cpu_percent} /></Table.Td>
        <Table.Td><MetricBar value={host.memory_percent} /></Table.Td>
        <Table.Td><MetricBar value={host.disk_percent} /></Table.Td>
        <Table.Td><Text size="sm">{formatBps(host.network_in_bps)}</Text></Table.Td>
        <Table.Td><Text size="sm">{formatBps(host.network_out_bps)}</Text></Table.Td>
      </Table.Tr>
      {expanded && (
        <Table.Tr>
          <Table.Td colSpan={columns} p={0}>
            <Collapse in={expanded}>
              <Box p="md" bg="var(--mantine-color-dark-7)">
                <Stack gap="xs">
                  <Text size="sm" fw={600}>Detalhes — {host.name}</Text>
                  <Group gap="lg">
                    <Text size="xs" c="dimmed">ID: {host.id}</Text>
                    <Text size="xs" c="dimmed">Status: {host.status}</Text>
                  </Group>
                  <Group gap="lg">
                    <Text size="xs">CPU: {(host.cpu_percent ?? 0).toFixed(1)}%</Text>
                    <Text size="xs">Memória: {(host.memory_percent ?? 0).toFixed(1)}%</Text>
                    <Text size="xs">Disco: {(host.disk_percent ?? 0).toFixed(1)}%</Text>
                  </Group>
                  <Group gap="lg">
                    <Text size="xs">Rede In: {formatBps(host.network_in_bps)} ({host.network_in_bps} bps)</Text>
                    <Text size="xs">Rede Out: {formatBps(host.network_out_bps)} ({host.network_out_bps} bps)</Text>
                  </Group>
                  {host.status === 'problem' && (
                    <Badge color="orange" size="sm" variant="light">
                      Host com problemas detectados pelo Zabbix
                    </Badge>
                  )}
                </Stack>
              </Box>
            </Collapse>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  )
}
